<template>
  <div class="configuration-settings">
    <!-- Registration Settings Section -->
    <v-card class="mb-6">
      <v-card-title>
        <v-icon start>mdi-cog</v-icon>
        Registration Settings
      </v-card-title>

      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-6">
          <strong>Registration Configuration</strong>
          <p class="text-caption mb-0 mt-2">
            Configure how users can register for the portal, including allowed email domains.
          </p>
        </v-alert>

        <v-form ref="configForm" @submit.prevent="saveConfig">
          <v-row>
            <!-- Registration Mode -->
            <v-col cols="12">
              <v-select
                v-model="config.registration"
                :items="registrationOptions"
                item-title="title"
                item-value="value"
                label="Registration Mode"
                variant="outlined"
                density="comfortable"
                hint="Controls how new users can register"
                persistent-hint
                :disabled="saving"
              ></v-select>
            </v-col>

            <!-- Allowed Email Domains -->
            <v-col cols="12">
              <v-combobox
                v-model="config.allowed_domains"
                :items="[]"
                label="Allowed Email Domains"
                variant="outlined"
                density="comfortable"
                multiple
                chips
                closable-chips
                hint="Email domains allowed for signup (e.g., 'brevardschools.org'). Leave empty to allow all domains."
                persistent-hint
                :disabled="saving"
                @update:model-value="cleanDomains"
              >
                <template v-slot:chip="{ props, item }">
                  <v-chip
                    v-bind="props"
                    color="primary"
                    variant="outlined"
                  >
                    @{{ item.value }}
                  </v-chip>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>
                      Type a domain (without @) and press <kbd>Enter</kbd>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-col>

            <!-- Domain Preview -->
            <v-col cols="12" v-if="config.allowed_domains && config.allowed_domains.length > 0">
              <v-alert type="info" variant="tonal" density="compact">
                <strong>Allowed signup emails:</strong>
                <span v-for="(domain, index) in config.allowed_domains" :key="domain">
                  {{ index > 0 ? ', ' : '' }}*@{{ domain }}
                </span>
              </v-alert>
            </v-col>

            <v-col cols="12" v-else>
              <v-alert type="warning" variant="tonal" density="compact">
                <strong>Warning:</strong> No domain restrictions set. Anyone with any email address can sign up.
              </v-alert>
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <div class="d-flex gap-3 mt-4">
            <v-btn
              color="primary"
              type="submit"
              :loading="saving"
              :disabled="!hasChanges"
            >
              <v-icon start>mdi-content-save</v-icon>
              Save Configuration
            </v-btn>

            <v-btn
              color="grey"
              variant="outlined"
              @click="loadConfig"
              :disabled="saving"
            >
              <v-icon start>mdi-refresh</v-icon>
              Reset
            </v-btn>
          </div>
        </v-form>

        <!-- Success/Error Messages -->
        <v-alert
          v-if="successMessage"
          type="success"
          variant="tonal"
          closable
          @click:close="successMessage = ''"
          class="mt-4"
        >
          {{ successMessage }}
        </v-alert>

        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          closable
          @click:close="errorMessage = ''"
          class="mt-4"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'

// Configuration State
const config = ref({
  registration: 'requireApproval',
  allowed_domains: [] as string[]
})

const originalConfig = ref({})
const saving = ref(false)
const loading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const registrationOptions = [
  { title: 'Open Registration - Anyone can create an account', value: 'open' },
  { title: 'Require Approval - Admin must approve new accounts', value: 'requireApproval' },
  { title: 'Disabled - Only admin can create accounts', value: 'none' }
]

const hasChanges = computed(() => {
  return JSON.stringify(config.value) !== JSON.stringify(originalConfig.value)
})

// Clean up domain entries (remove @ symbols, whitespace, etc.)
const cleanDomains = (domains: string[]) => {
  config.value.allowed_domains = domains
    .map(d => d.replace(/^@/, '').trim().toLowerCase())
    .filter(d => d.length > 0 && d.includes('.'))
}

// Load configuration
const loadConfig = async () => {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await api.get('/configuration')
    const data = response.data

    config.value = {
      registration: data.registration || 'requireApproval',
      allowed_domains: data.allowed_domains || []
    }

    originalConfig.value = JSON.parse(JSON.stringify(config.value))
  } catch (error: any) {
    console.error('Error loading config:', error)
    errorMessage.value = 'Failed to load configuration'
  } finally {
    loading.value = false
  }
}

// Save configuration
const saveConfig = async () => {
  saving.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    await api.put('/configuration', config.value)

    originalConfig.value = JSON.parse(JSON.stringify(config.value))
    successMessage.value = 'Configuration saved successfully!'
  } catch (error: any) {
    console.error('Error saving config:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to save configuration'
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadConfig()
})
</script>

<style scoped>
.configuration-settings {
  max-width: 1200px;
}

kbd {
  background-color: #eee;
  border-radius: 3px;
  border: 1px solid #b4b4b4;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  color: #333;
  display: inline-block;
  font-size: 0.85em;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  white-space: nowrap;
}
</style>
